import CryptoJS from "crypto-js";

const base64ToUint8Array = (b64: string): Uint8Array => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const uint8ArrayToBase64 = (bytes: Uint8Array): string => {
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    const sub = bytes.subarray(i, i + chunk);
    for (let j = 0; j < sub.length; j++) {
      bin += String.fromCharCode(sub[j]);
    }
  }
  return btoa(bin);
};

const importRsaPublicKeySpki = async (
  spkiBase64: string
): Promise<CryptoKey> => {
  const binary = base64ToUint8Array(spkiBase64);
  const keyBytes = new Uint8Array(binary.length);
  keyBytes.set(binary);
  return crypto.subtle.importKey(
    "spki",
    keyBytes,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"]
  );
};

export type EncryptedPayload = {
  data: Record<string, unknown>;
  encryptedKey: string;
  iv: string;
  encryptedFields: string[];
};

/**
 * Hybrid encryption: AES-256-GCM for field values, RSA-OAEP (SPKI public key) wraps the AES key.
 * `publicKeySpkiBase64` is PEM body only (base64), as returned by getSanitizedPublicKeyFromResponse.
 */
export async function encryptData(
  data: Record<string, unknown>,
  fields: string[],
  publicKeySpkiBase64: string
): Promise<EncryptedPayload> {
  const subtle = crypto.subtle;
  if (!subtle) {
    throw new Error("Web Crypto API is not available");
  }

  const iv = new Uint8Array(12);
  crypto.getRandomValues(iv);

  const aesKey = await subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt"]
  );

  const encryptedPayload: Record<string, unknown> = { ...data };

  for (const field of fields) {
    if (data[field] !== undefined && data[field] !== null) {
      const value =
        typeof data[field] === "string"
          ? String(data[field])
          : JSON.stringify(data[field]);

      const encoded = new TextEncoder().encode(value);

      const encrypted = await subtle.encrypt(
        { name: "AES-GCM", iv },
        aesKey,
        encoded
      );

      encryptedPayload[field] = uint8ArrayToBase64(new Uint8Array(encrypted));
    }
  }

  const exportedKey = await subtle.exportKey("raw", aesKey);

  const publicKey = await importRsaPublicKeySpki(publicKeySpkiBase64);

  const encryptedKeyBuffer = await subtle.encrypt(
    { name: "RSA-OAEP" },
    publicKey,
    exportedKey
  );

  return {
    data: encryptedPayload,
    encryptedKey: uint8ArrayToBase64(new Uint8Array(encryptedKeyBuffer)),
    iv: uint8ArrayToBase64(iv),
    encryptedFields: fields,
  };
};

export const encrypt = (dataText: any) => {
  const secretKey = process.env.REACT_APP_CRYPTO_SECRET_KEY ?? "";
  const payloadEnc: boolean =
    process.env.REACT_APP_PAYLOAD_ENCRYPTION === "true" || false;
  if (payloadEnc === false) {
    return dataText;
  }
  const encryptedData = CryptoJS.AES.encrypt(
    dataText?.toString(),
    secretKey ?? ""
  ).toString();
  return encryptedData;
};

export const decrypt = (cipherText: string) => {
  if (cipherText == null || cipherText === "") {
    return "";
  }
  const payloadEnc: boolean =
    process.env.REACT_APP_PAYLOAD_ENCRYPTION === "true" || false;
  if (payloadEnc === false) {
    return cipherText;
  }
  const secretKey = process.env.REACT_APP_CRYPTO_SECRET_KEY ?? "";
  return CryptoJS.AES.decrypt(cipherText, secretKey ?? "").toString(
    CryptoJS.enc.Utf8
  );
};

export const decodeJwtPayload = (token: string): any => {
  if (!token) return null;

  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) {
      console.log("Invalid JWT format: Missing payload segment.");
      return null;
    }

    let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4) {
      base64 += "=";
    }

    const jsonString = atob(base64);
    return JSON.parse(jsonString);
  } catch (e) {
    console.log("Error decoding or parsing JWT payload:", e);
    return null;
  }
};

export const encryptObject = (data: any) => {
  const secretKey = process.env.REACT_APP_CRYPTO_SECRET_KEY ?? "";
  const stringData = typeof data === "string" ? data : JSON.stringify(data); // Ensure string format
  const payloadEnc: boolean =
    process.env.REACT_APP_PAYLOAD_ENCRYPTION === "true" || false;
  if (payloadEnc === false) {
    return data;
  }
  const encryptedData = CryptoJS.AES.encrypt(
    stringData,
    secretKey ?? ""
  ).toString();
  return encryptedData;
};

export const decryptObject = (cipherText: string) => {
  const payloadEnc: boolean =
    process.env.REACT_APP_PAYLOAD_ENCRYPTION === "true" || false;
  if (payloadEnc === false) {
    return cipherText;
  }
  const secretKey = process.env.REACT_APP_CRYPTO_SECRET_KEY ?? "";
  const decryptedData = CryptoJS.AES.decrypt(
    cipherText,
    secretKey ?? ""
  ).toString(CryptoJS.enc.Utf8);

  try {
    return JSON.parse(decryptedData); // Attempt to parse JSON if data was originally an object
  } catch {
    return decryptedData; // Return as-is if it's a plain string
  }
};

export const decryptUrl = (encryptedBase64: string) => {
  try {
    const secretKey = process.env.REACT_APP_CRYPTO_SECRET_KEY ?? "";
    const bytes = CryptoJS.AES.decrypt(encryptedBase64.toString(), secretKey)?.toString(CryptoJS.enc.Utf8);
    return bytes;
  } catch (err) {
    console.error("Decryption failed:", err);
    return encryptedBase64;
  }
};


// export const encryptPaymentData = (dataText: any) => {
//   //const secretKey = process.env.REACT_APP_CRYPTO_SECRET_KEY ?? "";
//   const encryptedData = CryptoJS.AES.encrypt(
//     dataText.toString(),
//     documentsItOptions?.payment_secret_key
//   ).toString();
//   return encryptedData;
// };
// export const encryptPaymentData = (dataText: any) => {
//   //const secretKey = process.env.REACT_APP_CRYPTO_SECRET_KEY ?? "";
//   const payloadEnc: boolean = process.env.REACT_APP_PAYLOAD_ENCRYPTION === 'true' || false;
//   if(payloadEnc===false){
//     return dataText;
//   }
//   const secretKey = process.env.REACT_APP_CRYPTO_SECRET_KEY ?? "";
//   const encryptedData = CryptoJS.AES.encrypt(
//     dataText.toString(),
//     documentsItOptions?.payment_secret_key ?? ""
//   ).toString();
//   return encryptedData;
// };